"use client";

import React, { useState, useEffect, useMemo, useCallback, Suspense } from 'react';
import Link from 'next/link';
import { useStore } from '@/context/StoreContext';
import { useCart } from '@/context/CartContext';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faFilter, faTimes, faCartShopping, faChevronRight, faChevronDown, 
    faSearch, faList, faThLarge
} from '@fortawesome/free-solid-svg-icons';
import { debounce } from 'lodash';
import toast, { Toaster } from 'react-hot-toast';

import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { formatCurrency as formatPriceUtil } from '@/lib/formatters';
import { getSubdomain } from '@/lib/subdomain';
import { getApiBaseUrl } from '@/lib/api';

// --- Interfaces ---
interface ApiCategory {
    id: number;
    name: string;
    slug: string;
    image_url?: string | null;
    recursive_children?: ApiCategory[];
}

interface ApiVariant {
    id: number;
    sale_price: string;
    promotional_price: string | null;
    image_url?: string | null;
}

interface ApiProduct {
    id: number;
    name: string;
    image_url: string | null;
    description: string;
    category: { name: string; };
    starting_price: string | number | null;
    regular_starting_price: string | number | null;
    variants: ApiVariant[];
    total_stock: number;
}

interface PaginatedResponse {
    data: ApiProduct[];
    current_page: number;
    last_page: number;
    total: number;
    from: number;
    to: number;
}

interface FilterOptions {
    brands: string[];
    price_range: { min: number; max: number; };
}

const formatCurrency = (value: string | number | null, currencyCode?: string): string => {
    if (value === null || value === undefined) return 'Consulte';
    const numericValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numericValue)) return 'Consulte';
    return formatPriceUtil(numericValue, currencyCode || 'BRL');
};

const getInitials = (name: string) => name ? name.substring(0, 2).toUpperCase() : '';

const ProductCardGrid = ({ product }: { product: ApiProduct }) => {
    const { addToCart, openCart } = useCart();
    const { settings } = useStore();
    const formatPrice = (v: any) => formatCurrency(v, settings.currency);
    const isOnSale = product.regular_starting_price && product.starting_price !== product.regular_starting_price;
    const isOutOfStock = product.total_stock <= 0;

    const handleQuickAdd = (e: React.MouseEvent) => {
        e.preventDefault(); e.stopPropagation();
        if (isOutOfStock) return;

        if (product.variants && product.variants.length > 1) {
            window.location.href = `/produtos/${product.id}`;
            return;
        }

        const variant = product.variants?.[0];
        const variantId = variant?.id || product.id;
        const variantPrice = (variant?.promotional_price && parseFloat(variant.promotional_price) > 0)
            ? variant.promotional_price
            : (variant?.sale_price || product.starting_price || '0');

        addToCart({ 
            id: variantId, 
            name: product.name, 
            price: String(variantPrice), 
            imageUrl: variant?.image_url || product.image_url || '', 
            type: 'product_variant', 
            quantity: 1 
        });
        toast.success(`${product.name} adicionado!`, { icon: '🛒', style: { borderRadius: '10px', background: '#333', color: '#fff' } });
    };

    const handleBuyNow = (e: React.MouseEvent) => {
        e.preventDefault(); e.stopPropagation();
        if (isOutOfStock) return;

        if (product.variants && product.variants.length > 1) {
            window.location.href = `/produtos/${product.id}`;
            return;
        }

        const variant = product.variants?.[0];
        const variantId = variant?.id || product.id;
        const variantPrice = (variant?.promotional_price && parseFloat(variant.promotional_price) > 0)
            ? variant.promotional_price
            : (variant?.sale_price || product.starting_price || '0');

        addToCart({ 
            id: variantId, 
            name: product.name, 
            price: String(variantPrice), 
            imageUrl: variant?.image_url || product.image_url || '', 
            type: 'product_variant', 
            quantity: 1 
        });
        openCart();
    };

    return (
        <Link href={`/produtos/${product.id}`} className="block h-full group">
            <div className="product-card">
                <div className="product-image-container">
                    <img className={`product-image ${isOutOfStock ? 'grayscale opacity-60' : ''}`} src={product.image_url || 'https://placehold.co/400x400/f0f0f0/cccccc?text=Sem+Imagem'} alt={product.name} />
                    {isOnSale && !isOutOfStock && <div className="badge-sale">Oferta</div>}
                    {isOutOfStock && <div className="badge-stock">Esgotado</div>}
                </div>
                <div className="p-4 flex flex-col flex-grow">
                    <p className="text-xs text-gray-400 mb-1 uppercase tracking-wide truncate">{product.category?.name || 'Geral'}</p>
                    <h3 className="font-semibold text-gray-800 text-sm md:text-base leading-tight mb-2 line-clamp-2 min-h-[2.5em] group-hover:text-[var(--primary)] transition-colors">{product.name}</h3>
                    <div className="mt-auto pt-2 border-t border-gray-50">
                        {isOutOfStock ? ( <p className="text-sm font-bold text-gray-400">Indisponível</p> ) : (
                            <div className="flex flex-col">
                                {isOnSale && <span className="text-xs text-gray-400 line-through">{formatPrice(product.regular_starting_price)}</span>}
                                <div className="flex items-center justify-between mt-1 gap-2">
                                    <span className="font-bold text-[var(--primary)] text-lg truncate">{formatPrice(product.starting_price)}</span>
                                    <div className="flex items-center gap-1 shrink-0">
                                        <button onClick={handleBuyNow} className="hidden sm:block bg-[var(--primary)] text-white text-xs font-bold px-3 py-2 rounded-lg shadow-sm hover:brightness-110 active:scale-95 transition-all">
                                            Comprar
                                        </button>
                                        <button onClick={handleQuickAdd} className="w-8 h-8 rounded-lg bg-gray-100 text-[var(--primary)] flex items-center justify-center hover:bg-gray-200 active:scale-95 transition-all" title="Adicionar ao Carrinho">
                                            <FontAwesomeIcon icon={faCartShopping} className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Link>
    );
};

// --- 2. PRODUCT CARD LIST ---
const ProductCardList = ({ product }: { product: ApiProduct }) => {
    const { addToCart, openCart } = useCart();
    const { settings } = useStore();
    const formatPrice = (v: any) => formatCurrency(v, settings.currency);
    const isOnSale = product.regular_starting_price && product.starting_price !== product.regular_starting_price;
    const isOutOfStock = product.total_stock <= 0;

    const handleQuickAdd = (e: React.MouseEvent) => {
        e.preventDefault(); e.stopPropagation();
        if (isOutOfStock) return;

        if (product.variants && product.variants.length > 1) {
            window.location.href = `/produtos/${product.id}`;
            return;
        }

        const variant = product.variants?.[0];
        const variantId = variant?.id || product.id;
        const variantPrice = (variant?.promotional_price && parseFloat(variant.promotional_price) > 0)
            ? variant.promotional_price
            : (variant?.sale_price || product.starting_price || '0');

        addToCart({ 
            id: variantId, 
            name: product.name, 
            price: String(variantPrice), 
            imageUrl: variant?.image_url || product.image_url || '', 
            type: 'product_variant', 
            quantity: 1 
        });
        toast.success(`${product.name} adicionado!`, { icon: '🛒', style: { borderRadius: '10px', background: '#333', color: '#fff' } });
    };

    const handleBuyNow = (e: React.MouseEvent) => {
        e.preventDefault(); e.stopPropagation();
        if (isOutOfStock) return;

        if (product.variants && product.variants.length > 1) {
            window.location.href = `/produtos/${product.id}`;
            return;
        }

        const variant = product.variants?.[0];
        const variantId = variant?.id || product.id;
        const variantPrice = (variant?.promotional_price && parseFloat(variant.promotional_price) > 0)
            ? variant.promotional_price
            : (variant?.sale_price || product.starting_price || '0');

        addToCart({ 
            id: variantId, 
            name: product.name, 
            price: String(variantPrice), 
            imageUrl: variant?.image_url || product.image_url || '', 
            type: 'product_variant', 
            quantity: 1 
        });
        openCart();
    };

    return (
        <Link href={`/produtos/${product.id}`} className="block w-full cursor-pointer group">
            <div className={`bg-white rounded-2xl p-4 flex gap-6 border border-gray-100 shadow-sm transition-all duration-300 hover:shadow-md hover:border-[var(--primary)]/30 ${isOutOfStock ? 'opacity-70' : ''}`}>
                <div className="w-32 h-32 flex-shrink-0 bg-gray-50 rounded-xl p-2 relative overflow-hidden">
                    <img className="w-full h-full object-contain mix-blend-multiply" src={product.image_url || 'https://placehold.co/400x400/f0f0f0/333?text=Sem+Imagem'} alt={product.name} />
                    {isOnSale && !isOutOfStock && <div className="absolute top-2 left-2 bg-red-600 w-2 h-2 rounded-full"></div>}
                </div>
                <div className="flex-grow flex flex-col justify-center">
                    <div className="flex justify-between items-start">
                        <div>
                            <span className="text-xs text-gray-400 font-bold uppercase tracking-wider">{product.category?.name}</span>
                            <h3 className="text-lg font-bold text-gray-800 group-hover:text-[var(--primary)] transition-colors mb-2 line-clamp-1">{product.name}</h3>
                        </div>
                    </div>
                    <p className="text-sm text-gray-500 line-clamp-2 mb-4 hidden sm:block">{product.description}</p>
                    <div className="flex items-end justify-between mt-auto">
                        <div>
                            {isOutOfStock ? (
                                <span className="text-sm font-bold text-gray-400 bg-gray-100 px-3 py-1 rounded-full">Esgotado</span>
                            ) : (
                                <div className="flex items-baseline gap-2">
                                     {isOnSale && <span className="text-xs text-gray-400 line-through">{formatPrice(product.regular_starting_price)}</span>}
                                     <span className="text-xl font-bold text-[var(--primary)]">{formatPrice(product.starting_price)}</span>
                                </div>
                            )}
                        </div>
                        {!isOutOfStock && (
                            <div className="flex gap-2">
                                <button onClick={handleQuickAdd} className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg text-sm font-bold transition-colors">
                                    <FontAwesomeIcon icon={faCartShopping} />
                                </button>
                                <button onClick={handleBuyNow} className="bg-[var(--primary)] hover:brightness-110 text-white px-4 py-2 rounded-lg text-sm font-bold transition-colors flex items-center gap-2">
                                    Comprar
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Link>
    );
};

// --- COMPONENTE PRINCIPAL ---
function SearchPageComponent() {
    const { settings } = useStore();
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();

    const [products, setProducts] = useState<ApiProduct[]>([]);
    const [pagination, setPagination] = useState<Omit<PaginatedResponse, 'data'>>({ current_page: 1, last_page: 1, total: 0, from: 0, to: 0 });
    const [categories, setCategories] = useState<ApiCategory[]>([]);
    const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [expandedCategories, setExpandedCategories] = useState<Record<number, boolean>>({});
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

    useEffect(() => {
        const fetchFilterData = async () => {
            try {
                const subdomain = getSubdomain();
                const apiBaseUrl = getApiBaseUrl();
                const apiHeaders = { 'X-Tenant-Subdomain': subdomain, 'Accept': 'application/json' };

                const [categoriesRes, optionsRes] = await Promise.all([
                    fetch(`${apiBaseUrl}/public/categories`, { headers: apiHeaders }),
                    fetch(`${apiBaseUrl}/public/products/filter-options`, { headers: apiHeaders }),
                ]);

                if (categoriesRes.ok) setCategories(await categoriesRes.json());
                if (optionsRes.ok) setFilterOptions(await optionsRes.json());
            } catch (error) { console.error("Erro filtros:", error); }
        };
        fetchFilterData();
    }, []);
    
    useEffect(() => {
        const fetchProducts = async () => {
            setIsLoading(true);
            try {
                const subdomain = getSubdomain();
                const apiBaseUrl = getApiBaseUrl();
                const apiHeaders = { 'X-Tenant-Subdomain': subdomain, 'Accept': 'application/json' };
                const params = new URLSearchParams(searchParams.toString());
                const response = await fetch(`${apiBaseUrl}/public/products?${params.toString()}`, { headers: apiHeaders });

                if (!response.ok) throw new Error('Falha.');
                const data: PaginatedResponse = await response.json();
                setProducts(data.data);
                setPagination({ current_page: data.current_page, last_page: data.last_page, total: data.total, from: data.from, to: data.to });
            } catch (error) { console.error("Erro produtos:", error); } finally { setIsLoading(false); }
        };
        fetchProducts();
    }, [searchParams]);
    
    const updateFilters = useCallback((newParams: Record<string, string | null>) => {
        const current = new URLSearchParams(Array.from(searchParams.entries()));
        Object.entries(newParams).forEach(([key, value]) => {
            if (value === null || value === '') current.delete(key);
            else current.set(key, value);
        });
        if (newParams.page === undefined) current.delete('page');
        router.push(`${pathname}?${current.toString()}`, { scroll: false });
    }, [searchParams, router, pathname]);

    const debouncedUpdateFilters = useCallback(debounce(updateFilters, 500), [updateFilters]);

    const handleCategorySelect = (slug: string | null) => updateFilters({ category_slug: slug });
    const handleBrandChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const currentBrands = searchParams.get('brands')?.split(',') || [];
        const { value, checked } = e.target;
        const newBrands = checked ? [...currentBrands, value] : currentBrands.filter(b => b !== value);
        updateFilters({ brands: newBrands.length > 0 ? newBrands.join(',') : null });
    };
    const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => debouncedUpdateFilters({ [e.target.name]: e.target.value });
    const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => updateFilters({ sort_by: e.target.value });
    const handlePageChange = (page: number) => updateFilters({ page: page.toString() });
    const toggleCategoryExpansion = (categoryId: number) => setExpandedCategories(prev => ({ ...prev, [categoryId]: !prev[categoryId] }));

    // --- ÁRVORE DE CATEGORIAS (FICA DENTRO DO COMPONENTE PARA TER ACESSO AOS STATES) ---
    const CategoryTree = ({ items, level = 0 }: { items: ApiCategory[], level?: number }) => (
        <div className="space-y-1.5">
            {items.map(category => {
                const hasChildren = category.recursive_children && category.recursive_children.length > 0;
                const isExpanded = expandedCategories[category.id];
                const isSelected = searchParams.get('category_slug') === category.slug;

                return (
                    <div key={category.id}>
                        <div 
                            className={`w-full text-left py-2 px-3 rounded-xl text-sm transition-all flex items-center justify-between cursor-pointer select-none border border-transparent
                                ${isSelected ? 'bg-[var(--primary)]/10 text-[var(--primary)] font-bold border-[var(--primary)]/20' : 'text-gray-600 hover:bg-gray-50'}`}
                            style={{ marginLeft: `${level * 1}rem` }}
                            onClick={() => hasChildren ? toggleCategoryExpansion(category.id) : handleCategorySelect(category.slug)}
                        >
                            <div className="flex items-center gap-3 overflow-hidden" onClick={(e) => { e.stopPropagation(); handleCategorySelect(category.slug); }}>
                                <div className={`w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden border ${isSelected ? 'border-[var(--primary)]/30 bg-white' : 'border-gray-200 bg-white shadow-sm'}`}>
                                    {category.image_url ? (
                                        <img src={category.image_url} alt={category.name} className="w-full h-full object-cover" />
                                    ) : (
                                        <span className={`text-[10px] font-black ${isSelected ? 'text-[var(--primary)]' : 'text-gray-400'}`}>{getInitials(category.name)}</span>
                                    )}
                                </div>
                                <span className="truncate">{category.name}</span>
                            </div>
                            {hasChildren && <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} className={`h-3 w-3 flex-shrink-0 ${isSelected ? 'text-[var(--primary)]' : 'text-gray-400'}`} />}
                        </div>
                        {hasChildren && isExpanded && (
                            <div className="mt-2 border-l-2 border-gray-100 ml-4 pl-2">
                                <CategoryTree items={category.recursive_children || []} level={level + 1} />
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );

    const activeFiltersCount = useMemo(() => Array.from(searchParams.keys()).filter(k => k !== 'page').length, [searchParams]);

    return (
        <div className="bg-[var(--page-bg,#fff8f6)] min-h-screen flex flex-col">
             <Toaster position="top-center" />
             
             {/* 1. Header Global em vez de código duplicado */}
             <Header 
                searchValue={searchParams.get('search') || ''} 
                onSearchChange={(val) => debouncedUpdateFilters({ search: val })} 
             />
            
            <div className="container mx-auto px-4 py-8 flex gap-8 items-start flex-grow">
                {/* --- SIDEBAR DE FILTROS (Desktop) --- */}
                <aside className="w-1/4 hidden lg:block sticky top-28">
                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-lg font-bold text-gray-800">Filtros</h2>
                            {activeFiltersCount > 0 && <button onClick={() => router.push(pathname)} className="text-xs text-red-500 hover:underline font-medium">Limpar</button>}
                        </div>
                        
                        <div className="mb-8">
                            <h3 className="font-bold text-sm text-gray-400 uppercase tracking-wider mb-4">Categorias</h3>
                            <button onClick={() => handleCategorySelect(null)} className={`w-full text-left py-2 px-3 rounded-lg text-sm mb-2 transition-all ${!searchParams.get('category_slug') ? 'bg-[var(--primary)] text-white font-semibold shadow-md' : 'text-gray-600 hover:bg-gray-100'}`}>
                                Todas as Categorias
                            </button>
                            <CategoryTree items={categories} />
                        </div>

                        {filterOptions && filterOptions.brands.length > 0 && (
                            <div className="mb-8">
                                <h3 className="font-bold text-sm text-gray-400 uppercase tracking-wider mb-4">Marcas</h3>
                                <div className="space-y-2 max-h-48 overflow-y-auto pr-2 scrollbar-thin">
                                    {filterOptions.brands.map(brand => (
                                        <label key={brand} className="flex items-center gap-3 text-sm text-gray-600 cursor-pointer hover:text-[var(--primary)] transition-colors">
                                            <input type="checkbox" value={brand} checked={searchParams.get('brands')?.split(',').includes(brand)} onChange={handleBrandChange} className="w-4 h-4 rounded border-gray-300 text-[var(--primary)] focus:ring-[var(--primary)]" />
                                            {brand}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}

                        {filterOptions && (
                            <div>
                                <h3 className="font-bold text-sm text-gray-400 uppercase tracking-wider mb-4">Preço</h3>
                                <div className="flex gap-2 items-center">
                                    <input type="number" name="min_price" placeholder="Min" defaultValue={searchParams.get('min_price') || ''} onChange={handlePriceChange} className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--primary)]" />
                                    <span className="text-gray-400">-</span>
                                    <input type="number" name="max_price" placeholder="Max" defaultValue={searchParams.get('max_price') || ''} onChange={handlePriceChange} className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--primary)]" />
                                </div>
                            </div>
                        )}
                    </div>
                </aside>

                {/* --- CONTEÚDO PRINCIPAL --- */}
                <main className="w-full lg:w-3/4">
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
                        <div className="text-sm font-medium text-gray-500">
                           {isLoading 
                               ? 'Carregando produtos...' 
                               : (pagination.total > 0 ? <span>Mostrando <strong>{pagination.from}-{pagination.to}</strong> de <strong>{pagination.total}</strong> produtos</span> : 'Nenhum produto encontrado')
                           }
                        </div>
                        
                        <div className="flex items-center justify-between sm:justify-end gap-3 w-full sm:w-auto">
                            <button onClick={() => setIsSidebarOpen(true)} className="lg:hidden bg-gray-100 hover:bg-gray-200 px-4 py-2.5 rounded-lg flex items-center gap-2 text-sm font-bold text-gray-700 transition-colors">
                                <FontAwesomeIcon icon={faFilter} className="h-4 w-4" />
                                Filtros
                                {activeFiltersCount > 0 && <span className="bg-[var(--primary)] text-white text-[10px] rounded-full h-5 w-5 flex items-center justify-center">{activeFiltersCount}</span>}
                            </button>
                            
                            <div className="relative">
                                <select onChange={handleSortChange} defaultValue={searchParams.get('sort_by') || 'latest'} className="appearance-none bg-gray-100 hover:bg-gray-200 pl-4 pr-10 py-2.5 rounded-lg text-sm font-bold text-gray-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[var(--primary)] transition-colors">
                                    <option value="latest">Mais Recentes</option>
                                    <option value="price_asc">Menor Preço</option>
                                    <option value="price_desc">Maior Preço</option>
                                </select>
                                <FontAwesomeIcon icon={faChevronDown} className="absolute right-3 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-500 pointer-events-none" />
                            </div>

                            <div className="flex bg-gray-100 p-1 rounded-lg">
                                <button onClick={() => setViewMode('grid')} className={`p-2 rounded-md transition-all ${viewMode === 'grid' ? 'bg-white text-[var(--primary)] shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}>
                                    <FontAwesomeIcon icon={faThLarge} className="h-4 w-4" />
                                </button>
                                <button onClick={() => setViewMode('list')} className={`p-2 rounded-md transition-all ${viewMode === 'list' ? 'bg-white text-[var(--primary)] shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}>
                                    <FontAwesomeIcon icon={faList} className="h-4 w-4" />
                                </button>
                           </div>
                        </div>
                    </div>

                    {isLoading ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
                            {Array.from({ length: 8 }).map((_, i) => <div key={i} className="bg-white aspect-[3/4] rounded-2xl animate-pulse"></div>)}
                        </div>
                    ) : products.length > 0 ? (
                        viewMode === 'grid' ? (
                            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
                                {products.map((product, index) => (
                                    <div key={product.id} className="animate-fadeIn" style={{ animationDelay: `${index * 50}ms` }}>
                                        <ProductCardGrid product={product} />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="flex flex-col gap-4">
                                {products.map((product, index) => (
                                    <div key={product.id} className="animate-fadeIn" style={{ animationDelay: `${index * 50}ms` }}>
                                        <ProductCardList product={product} />
                                    </div>
                                ))}
                            </div>
                        )
                    ) : (
                        <div className="text-center py-32 bg-white rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center justify-center">
                            <div className="bg-gray-50 p-6 rounded-full mb-4"><FontAwesomeIcon icon={faSearch} className="h-10 w-10 text-gray-300" /></div>
                            <h3 className="text-xl font-bold text-gray-800 mb-2">Nenhum produto encontrado</h3>
                            <p className="text-gray-500 mb-6">Tente ajustar os filtros ou buscar por outro termo.</p>
                            <button onClick={() => router.push(pathname)} className="px-6 py-2.5 bg-gray-100 text-gray-700 font-bold rounded-lg hover:bg-gray-200 transition-colors">Limpar Filtros</button>
                        </div>
                    )}
                    
                    {pagination.last_page > 1 && (
                        <div className="flex justify-center mt-12 gap-2">
                            {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map(page => (
                                <button
                                    key={page} onClick={() => handlePageChange(page)}
                                    className={`w-10 h-10 rounded-lg text-sm font-bold transition-all ${pagination.current_page === page ? 'bg-[var(--primary)] text-white shadow-lg shadow-[var(--primary)]/30 scale-110' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}
                                >
                                    {page}
                                </button>
                            ))}
                        </div>
                    )}
                </main>
            </div>
            
            {/* 2. Footer Global em vez de código duplicado */}
            <Footer />

            {/* --- MOBILE FILTER SIDEBAR --- */}
             <div className={`fixed inset-0 z-50 lg:hidden transition-opacity duration-300 ${isSidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsSidebarOpen(false)}></div>
                <div className={`absolute top-0 right-0 h-full w-4/5 max-w-xs bg-white shadow-2xl transform transition-transform duration-300 flex flex-col ${isSidebarOpen ? 'translate-x-0' : 'translate-x-full'}`}>
                    <div className="p-5 flex justify-between items-center border-b border-gray-100">
                        <h2 className="text-lg font-bold text-gray-800">Filtrar Resultados</h2>
                        <button onClick={() => setIsSidebarOpen(false)} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200"><FontAwesomeIcon icon={faTimes} className="h-4 w-4" /></button>
                    </div>
                    
                    <div className="overflow-y-auto flex-grow p-6">
                       <div className="mb-8">
                           <h3 className="font-bold text-sm text-gray-400 uppercase tracking-wider mb-4">Categorias</h3> 
                           <button onClick={() => {handleCategorySelect(null); setIsSidebarOpen(false);}} className={`w-full text-left py-2 px-3 rounded-lg text-sm mb-2 transition-all ${!searchParams.get('category_slug') ? 'bg-[var(--primary)] text-white font-semibold' : 'text-gray-600 bg-gray-50 hover:bg-gray-100'}`}>Todas as Categorias</button>
                           <CategoryTree items={categories} />
                       </div>
                       
                       {filterOptions && filterOptions.brands.length > 0 && (
                           <div className="mb-8">
                               <h3 className="font-bold text-sm text-gray-400 uppercase tracking-wider mb-4">Marcas</h3> 
                               <div className="space-y-3">
                                   {filterOptions.brands.map(brand => (
                                       <label key={brand} className="flex items-center gap-3 text-sm text-gray-700">
                                           <input type="checkbox" value={brand} checked={searchParams.get('brands')?.split(',').includes(brand)} onChange={handleBrandChange} className="w-5 h-5 rounded border-gray-300 text-[var(--primary)] focus:ring-[var(--primary)]" />
                                           {brand}
                                       </label>
                                   ))}
                               </div>
                           </div>
                       )}
                       
                       {filterOptions && (
                           <div>
                               <h3 className="font-bold text-sm text-gray-400 uppercase tracking-wider mb-4">Preço</h3> 
                               <div className="flex gap-3 items-center">
                                   <input type="number" name="min_price" placeholder="Min" defaultValue={searchParams.get('min_price') || ''} onChange={handlePriceChange} className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:border-[var(--primary)]" />
                                   <span className="text-gray-400">-</span>
                                   <input type="number" name="max_price" placeholder="Max" defaultValue={searchParams.get('max_price') || ''} onChange={handlePriceChange} className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:border-[var(--primary)]" />
                               </div>
                           </div>
                       )}
                    </div>
                    
                    <div className="p-5 border-t border-gray-100 bg-gray-50">
                       <button onClick={() => setIsSidebarOpen(false)} className="w-full bg-[var(--primary)] hover:brightness-110 text-white font-bold py-3.5 rounded-xl shadow-lg shadow-[var(--primary)]/20 transition-all">
                           Ver {pagination.total} Resultados
                       </button>
                    </div>
                </div>
             </div>
        </div>
    );
}

export default function SearchPage() {
    return (
        <Suspense fallback={<div className="flex h-screen items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-orange-500"></div></div>}>
            <SearchPageComponent />
        </Suspense>
    );
}